import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';

export interface CodebookItem {
  id?: string;
  code: string;
  companyName?: string;
  idNumber?: string;
  vatNumber?: string;
  clientID?: string;
}

/**
 * Mirrors the backend codeBookIntegration() logic from folderInvoicesExportService.
 *
 * Matching rule: after normalizing (removing non-alphanumeric characters and
 * lowercasing), the invoice field must exactly equal the codebook field.
 * This prevents false positives from 70% partial matching (slice(0, -4)).
 *
 * Priority: ID (idNumber) first, then vatID (vatNumber).
 */
@Injectable({ providedIn: 'root' })
export class CodebookLookupService {

  private normalizeId(value: string): string {
    if (!value) return '';
    return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  /**
   * Returns the code for the first matching codebook entry, or '' if not found.
   * Tries to match by full normalized ID first, then by full normalized vatID.
   * Falls back to fuzzy company name matching via lookupCodeByCompanyName.
   */
  lookupCode(items: CodebookItem[], idValue: string, vatValue: string): string {
    const priorities = [
      { field: 'idNumber' as keyof CodebookItem, invoiceVal: idValue },
      { field: 'vatNumber' as keyof CodebookItem, invoiceVal: vatValue },
    ];

    for (const p of priorities) {
      const invoiceKey = this.normalizeId(p.invoiceVal);
      if (!invoiceKey) continue;
      const found = items.find((item) => {
        const cbField = item[p.field] as string | undefined;
        if (!cbField) return false;
        return this.normalizeId(cbField) === invoiceKey;
      });
      if (found) return found.code || '';
    }
    return '';
  }

  /**
   * Strips common company suffixes (d.o.o., doo, d.d., dd, a.d., etc.),
   * common Bosnian city/location suffixes (Sarajevo, Tuzla, Banja Luka, etc.),
   * and extra whitespace so that matching focuses on the actual company name.
   */
  private stripCompanySuffix(name: string): string {
    const legalSuffixes = /\b(d\.?\s*o\.?\s*o\.?|doo|d\.?\s*d\.?|dd|a\.?\s*d\.?|ad)\b\.?/gi;
    let cleaned = name.replace(legalSuffixes, '').replace(/[.,]+$/g, '').trim();

    const cities = [
      'sarajevo', 'tuzla', 'banja luka', 'mostar', 'zenica', 'bihac', 'gorazde',
      'livno', 'doboj', 'prijedor', 'bijeljina', 'brcko', 'travnik', 'visoko',
      'igman', 'ilidza', 'stari grad', 'novo sarajevo', 'novi grad', 'cengic vila',
      'gradiska', 'gracanica', 'odzak', 'orascic', 'kakanj', 'konjic',
      'novi travnik', 'vitez', 'kiseljak', 'bugojno', 'jajce', 'siroki brijeg',
      'ljubuski', 'capljina', 'stolac', 'trebinje', 'focha', 'foča', 'gacko',
      'nevesinje', 'bileca', 'ravno', 'berkovici', 'hrasnici'
    ];
    const cityPattern = new RegExp(`\\b(?:${cities.join('|')})\\b`, 'gi');
    cleaned = cleaned.replace(cityPattern, '').replace(/\s+/g, ' ').trim().replace(/[.,]+$/g, '');
    return cleaned;
  }

  /**
   * Fuzzy fallback: matches the invoice company name against codebook entries.
   * Strips common suffixes (d.o.o., doo, d.d., dd), ignores case, then checks:
   *   1. Containment (one stripped name contains the other)
   *   2. Dice-coefficient bigram similarity >= threshold (default 0.7)
   */
  lookupCodeByCompanyName(items: CodebookItem[], companyName: string, threshold = 0.7): string {
    if (!companyName?.trim() || companyName.trim().length < 4) return '';
    const query = this.stripCompanySuffix(companyName.trim().toLowerCase());
    if (query.length < 4) return '';
    let bestScore = 0;
    let bestCode = '';
    for (const item of items) {
      if (!item.companyName || !item.code) continue;
      const cbName = this.stripCompanySuffix(item.companyName.trim().toLowerCase());
      if (cbName.length < 4) continue;
      // Containment check: one name contains the other (immediate match)
      if (cbName.includes(query) || query.includes(cbName)) {
        return item.code;
      }
      // Dice similarity fallback
      const sim = this.stringSimilarity(query, cbName);
      if (sim > bestScore) {
        bestScore = sim;
        bestCode = item.code;
      }
    }
    return bestScore >= threshold ? bestCode : '';
  }

  private stringSimilarity(a: string, b: string): number {
    if (a === b) return 1;
    if (a.length < 2 || b.length < 2) return a === b ? 1 : 0;
    const bigrams = (s: string): Map<string, number> => {
      const m = new Map<string, number>();
      for (let i = 0; i < s.length - 1; i++) {
        const bg = s.slice(i, i + 2);
        m.set(bg, (m.get(bg) || 0) + 1);
      }
      return m;
    };
    const aBg = bigrams(a);
    const bBg = bigrams(b);
    let intersection = 0;
    aBg.forEach((cnt, bg) => { intersection += Math.min(cnt, bBg.get(bg) || 0); });
    const total = (a.length - 1) + (b.length - 1);
    return total > 0 ? (2 * intersection) / total : 0;
  }

  async listAllByClientID(clientID: string): Promise<CodebookItem[]> {
    const statement = `
      query GetCodebookByClientID($clientID: ID!, $limit: Int, $nextToken: String) {
        getCodebookByClientID(clientID: $clientID, limit: $limit, nextToken: $nextToken) {
          items {
            id
            code
            companyName
            idNumber
            vatNumber
            clientID
          }
          nextToken
        }
      }
    `;

    let allItems: CodebookItem[] = [];
    let token: string | null = null;

    do {
      const vars: any = { clientID, limit: 10000 };
      if (token) vars.nextToken = token;

      const res = (await API.graphql(graphqlOperation(statement, vars))) as any;
      const page = res?.data?.getCodebookByClientID;
      allItems = allItems.concat(page?.items ?? []);
      token = page?.nextToken ?? null;
    } while (token);

    return allItems;
  }
}
